import React, { useState, useEffect } from 'react';
import {
  Menu,
  Box,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import CloseIcon from '@mui/icons-material/Close';
import clickTracker from './utils/ClickTracker';

export default function PrismClickHistoryModal({ anchorEl, open, onClose }) {
  const [clicks, setClicks] = useState([]);

  useEffect(() => {
    if (open) {
      // Load clicks when popover opens
      setClicks(clickTracker.getClicks());
      
      // Subscribe to updates
      const unsubscribe = clickTracker.subscribe((newClicks) => {
        setClicks([...newClicks]);
      });
      
      return unsubscribe;
    }
  }, [open]);

  const handleClear = () => {
    if (window.confirm('Clear all click history?')) {
      clickTracker.clear();
    }
  };

  const handleExport = () => {
    const json = clickTracker.exportJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prism-clicks-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now - then) / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          mt: 1,
          width: 480,
          maxHeight: '600px',
          bgcolor: 'rgba(18, 24, 54, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(121, 184, 201, 0.3)',
          borderRadius: '12px',
          color: 'white',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <TouchAppIcon sx={{ color: 'rgba(121, 184, 201, 1)', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600} color="rgba(121, 184, 201, 1)">
              Click History
            </Typography>
            <Chip 
              label={`${clicks.length} clicks`} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(121, 184, 201, 0.2)',
                color: 'rgba(121, 184, 201, 1)',
                border: '1px solid rgba(121, 184, 201, 0.3)',
                fontSize: '10px',
                fontWeight: 600,
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              onClick={handleExport}
              disabled={clicks.length === 0}
              sx={{ 
                color: 'rgba(91, 158, 130, 1)',
                '&:hover': { bgcolor: 'rgba(91, 158, 130, 0.1)' },
                '&:disabled': { color: 'rgba(255,255,255,0.3)' },
              }}
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleClear}
              disabled={clicks.length === 0}
              sx={{ 
                color: 'rgba(244, 67, 54, 1)',
                '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' },
                '&:disabled': { color: 'rgba(255,255,255,0.3)' },
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Click List */}
      <List sx={{ p: 0, maxHeight: '450px', overflowY: 'auto' }}>
        {clicks.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            gap={2}
          >
            <TouchAppIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
            <Typography color="rgba(255,255,255,0.7)" variant="body2">
              No clicks recorded yet
            </Typography>
            <Typography color="rgba(255,255,255,0.5)" variant="caption">
              Start interacting with the EMR!
            </Typography>
          </Box>
        ) : (
          clicks.slice().reverse().slice(0, 20).map((click, index) => (
            <React.Fragment key={index}>
              <ListItem 
                sx={{ 
                  py: 1.5,
                  px: 2,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } 
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight={500} color="white">
                        {click.label}
                      </Typography>
                      <Chip 
                        label={click.section} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'rgba(91, 158, 130, 0.2)',
                          color: 'rgba(91, 158, 130, 1)',
                          border: '1px solid rgba(91, 158, 130, 0.3)',
                          fontSize: '10px',
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="rgba(255,255,255,0.6)">
                      {click.text && click.text.length > 0 && click.text !== click.label && (
                        <span>{click.text.substring(0, 60)}... • </span>
                      )}
                      {formatRelativeTime(click.timestamp)}
                    </Typography>
                  }
                />
              </ListItem>
              {index < Math.min(clicks.length - 1, 19) && (
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
              )}
            </React.Fragment>
          ))
        )}
      </List>
      
      {clicks.length > 20 && (
        <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="caption" color="rgba(255,255,255,0.5)">
            Showing 20 of {clicks.length} clicks
          </Typography>
        </Box>
      )}
    </Menu>
  );
}
